'use client';

// UrlPreviewCard - Display extracted URL content with preview and status

import { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Alert from '@mui/material/Alert';
import { useTranslations } from 'next-intl';
import DeleteIcon from '@mui/icons-material/Delete';
import LinkIcon from '@mui/icons-material/Link';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import RefreshIcon from '@mui/icons-material/Refresh';

export type UrlStatus = 'pending' | 'extracting' | 'ready' | 'partial' | 'error';

export interface ExtractedUrl {
  /** Unique identifier for the URL (local tracking ID) */
  id: string;
  /** Database content ID (for deletion sync) */
  contentId?: string;
  /** The original URL */
  url: string;
  /** Extracted page title */
  title?: string;
  /** Preview of extracted content (first 500 chars) */
  excerpt?: string;
  /** Full extracted content */
  content?: string;
  /** Character count of extracted content */
  characterCount?: number;
  /** Current processing status */
  status: UrlStatus;
  /** Whether extraction was partial (some content may be missing) */
  isPartial?: boolean;
  /** Error message if extraction failed */
  errorMessage?: string;
  /** Error code for i18n lookup */
  errorCode?: string;
  /** Extraction method used */
  extractionMethod?: 'static' | 'browser';
  /** Link score if this was a crawled page */
  linkScore?: number;
  /** Original anchor text if crawled from parent page */
  linkText?: string;
}

export interface UrlPreviewCardProps {
  /** The extracted URL data */
  url: ExtractedUrl;
  /** Callback to remove the URL */
  onRemove: (urlId: string) => void;
  /** Callback to retry failed extraction */
  onRetry?: (urlId: string) => void;
  /** Whether the card is disabled */
  disabled?: boolean;
}

/**
 * Truncates a URL for display
 */
function truncateUrl(url: string, maxLength: number = 60): string {
  if (url.length <= maxLength) return url;
  const start = url.substring(0, maxLength - 10);
  const end = url.substring(url.length - 7);
  return `${start}...${end}`;
}

/**
 * UrlPreviewCard - Displays an extracted URL with its content preview
 *
 * Features:
 * - Page title and URL display
 * - Character count of extracted content
 * - Expandable content preview
 * - Partial extraction warning
 * - Remove button
 * - Error state display
 * - RTL support via CSS logical properties
 *
 * @example
 * ```tsx
 * <UrlPreviewCard
 *   url={extractedUrl}
 *   onRemove={(id) => removeUrl(id)}
 * />
 * ```
 */
export function UrlPreviewCard({
  url,
  onRemove,
  onRetry,
  disabled = false,
}: UrlPreviewCardProps) {
  const t = useTranslations('creator.urlExtraction');
  const [isExpanded, setIsExpanded] = useState(false);

  const isLoading = url.status === 'pending' || url.status === 'extracting';
  const hasError = url.status === 'error';
  const isPartial = url.status === 'partial' || url.isPartial;

  // Determine border color based on status
  const getBorderColor = () => {
    if (hasError) return 'error.light';
    if (isPartial) return 'warning.light';
    if (url.status === 'ready') return 'success.light';
    return 'divider';
  };

  // Determine background color based on status
  const getBackgroundColor = () => {
    if (hasError) return 'error.lighter';
    return 'background.paper';
  };

  // Get status icon
  const getStatusIcon = () => {
    if (url.status === 'ready' && !isPartial) {
      return <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />;
    }
    if (isPartial) {
      return <WarningIcon sx={{ color: 'warning.main', fontSize: 20 }} />;
    }
    if (hasError) {
      return <ErrorIcon sx={{ color: 'error.main', fontSize: 20 }} />;
    }
    return null;
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        border: '1px solid',
        borderColor: getBorderColor(),
        borderRadius: 1,
        backgroundColor: getBackgroundColor(),
      }}
    >
      {/* Header row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.5,
        }}
      >
        {/* Link icon */}
        <Box sx={{ flexShrink: 0, pt: 0.25 }}>
          <LinkIcon sx={{ color: hasError ? 'error.main' : 'primary.main' }} />
        </Box>

        {/* URL info */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            fontWeight="medium"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {url.title || truncateUrl(url.url)}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            component="a"
            href={url.url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            {truncateUrl(url.url)}
          </Typography>
        </Box>

        {/* Status indicator */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          {getStatusIcon()}
        </Box>

        {/* Remove button */}
        <IconButton
          size="small"
          onClick={() => onRemove(url.id)}
          disabled={disabled || isLoading}
          aria-label={t('preview.removeUrl')}
          sx={{
            flexShrink: 0,
            '&:hover': {
              color: 'error.main',
            },
          }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Progress bar for loading state */}
      {isLoading && (
        <Box sx={{ mt: 1.5 }}>
          <LinearProgress variant="indeterminate" sx={{ borderRadius: 1 }} />
        </Box>
      )}

      {/* Error message */}
      {hasError && url.errorMessage && (
        <Box sx={{ mt: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="caption" color="error">
                {t(`error.${url.errorCode || 'extractionFailed'}`)}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {t('error.suggestion')}
              </Typography>
            </Box>
            {onRetry && (
              <IconButton
                size="small"
                onClick={() => onRetry(url.id)}
                disabled={disabled}
                aria-label={t('preview.retry')}
                sx={{ ml: 1, color: 'primary.main' }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        </Box>
      )}

      {/* Partial extraction warning */}
      {isPartial && !hasError && (
        <Alert
          severity="warning"
          sx={{ mt: 1.5, py: 0.5 }}
          icon={<WarningIcon fontSize="small" />}
        >
          <Typography variant="caption">
            {t('preview.partialWarning')}
          </Typography>
        </Alert>
      )}

      {/* Content preview */}
      {(url.status === 'ready' || url.status === 'partial') && url.excerpt && (
        <Box sx={{ mt: 1.5 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 0.5,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {t('preview.charactersExtracted', { count: url.characterCount || url.content?.length || 0 })}
            </Typography>
            <IconButton
              size="small"
              onClick={() => setIsExpanded(!isExpanded)}
              aria-label={t('preview.editContent')}
              sx={{ ml: 1 }}
            >
              {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </IconButton>
          </Box>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              backgroundColor: 'action.hover',
              p: 1,
              borderRadius: 1,
              fontSize: '0.75rem',
              lineHeight: 1.4,
              maxHeight: isExpanded ? 'none' : 80,
              overflow: 'hidden',
              display: isExpanded ? 'block' : '-webkit-box',
              WebkitLineClamp: isExpanded ? 'unset' : 3,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {url.excerpt}
          </Typography>
        </Box>
      )}

      {/* Link origin info (for crawled pages) */}
      {url.linkText && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          {t('preview.crawledFrom', { url: url.linkText })}
        </Typography>
      )}
    </Paper>
  );
}

/**
 * UrlPreviewList - Displays a list of extracted URLs
 */
export interface UrlPreviewListProps {
  /** List of extracted URLs */
  urls: ExtractedUrl[];
  /** Callback to remove a URL */
  onRemove: (urlId: string) => void;
  /** Callback to retry failed extraction */
  onRetry?: (urlId: string) => void;
  /** Whether the list is disabled */
  disabled?: boolean;
}

export function UrlPreviewList({
  urls,
  onRemove,
  onRetry,
  disabled = false,
}: UrlPreviewListProps) {
  if (urls.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        mt: 2,
      }}
    >
      {urls.map((url) => (
        <UrlPreviewCard
          key={url.id}
          url={url}
          onRemove={onRemove}
          {...(onRetry ? { onRetry } : {})}
          disabled={disabled}
        />
      ))}
    </Box>
  );
}
